import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, apiPost } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import ActiveScopeChip from '@/components/ActiveScopeChip';
import BuildingDeckSplash from '@/components/BuildingDeckSplash';

export default function FlashcardsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('list'); // list, select, viewing
  const [sets, setSets] = useState([]);
  const [activeSet, setActiveSet] = useState(null);
  const [cardIdx, setCardIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(() => {
    // Phase 2.71 — pre-fill from the top-bar dropdown so the student
    // doesn't re-pick what they already chose.
    try { return window.localStorage.getItem('gv_active_subject') || ''; }
    catch { return ''; }
  });
  const [selectedTopic, setSelectedTopic] = useState(() => {
    try { return window.localStorage.getItem('gv_active_topic') || ''; }
    catch { return ''; }
  });
  const [loading, setLoading] = useState(false);
  // Phase 2.70 — scope chip. Snapshots localStorage so the list stays
  // stable while the student browses, and Clear filter reverts to the
  // full library locally without touching the global top-bar selection.
  const [scopeSubject, setScopeSubject] = useState(() => {
    try { return window.localStorage.getItem('gv_active_subject') || ''; }
    catch { return ''; }
  });
  const [scopeTopic, setScopeTopic] = useState(() => {
    try { return window.localStorage.getItem('gv_active_topic') || ''; }
    catch { return ''; }
  });
  const [autoHydrating, setAutoHydrating] = useState(false);
  const autoHydratedRef = useRef(false);

  useEffect(() => {
    const parts = [];
    if (scopeSubject) parts.push(`subject=${encodeURIComponent(scopeSubject)}`);
    if (scopeTopic) parts.push(`topic=${encodeURIComponent(scopeTopic)}`);
    const qs = parts.length ? `?${parts.join('&')}` : '';
    apiGet(`/flashcards/sets${qs}`).then(async d => {
      const list = d.sets || [];
      setSets(list);
      // Phase 2.72 + 2.73 — Auto-hydrate from cache with a visible splash
      // so the student doesn't think they need to click Generate.
      if (
        list.length === 0
        && scopeSubject
        && scopeTopic
        && user?.class_level
        && !autoHydratedRef.current
      ) {
        autoHydratedRef.current = true;
        setAutoHydrating(true);
        const startTs = performance.now();
        try {
          const hydrated = await apiPost('/flashcards/generate', {
            subject: scopeSubject,
            topic: scopeTopic,
            board: user?.board || '',
            class_level: String(user.class_level || ''),
          });
          if (hydrated && hydrated.set_id) {
            setSets([hydrated]);
            // Phase 2.73 — Felt-speed toast. Only when hydration actually
            // produced a set, so we don't claim success on a silent fail.
            const elapsedS = Math.max(0.1, (performance.now() - startTs) / 1000);
            toast.success(
              `Ready in ${elapsedS.toFixed(1)}s — flashcards pre-assembled for ${scopeSubject} · ${scopeTopic}`,
              { duration: 2400 },
            );
          }
        } catch { /* cache empty / tier-blocked */ }
        finally { setAutoHydrating(false); }
      }
    }).catch(() => {});
    if (user?.class_level) {
      const boardParam = user?.board ? `&board=${encodeURIComponent(user.board)}` : '';
      apiGet(`/onboarding/subjects?class_level=${user.class_level}${boardParam}`).then(d => setSubjects(d.subjects || [])).catch(() => {});
    }
  }, [user, scopeSubject, scopeTopic]);

  useEffect(() => {
    if (selectedSubject && user?.class_level) {
      const boardParam = user?.board ? `&board=${encodeURIComponent(user.board)}` : '';
      apiGet(`/onboarding/topics?subject=${encodeURIComponent(selectedSubject)}&class_level=${user.class_level}${boardParam}`).then(d => setTopics(d.topics || [])).catch(() => {});
    }
  }, [selectedSubject, user]);

  const generateSet = async () => {
    if (!selectedSubject || !selectedTopic) { toast.error('Select subject and topic'); return; }
    setLoading(true);
    try {
      const set = await apiPost('/flashcards/generate', { subject: selectedSubject, topic: selectedTopic });
      setSets(prev => [set, ...prev]);
      setActiveSet(set);
      setCardIdx(0);
      setFlipped(false);
      setPhase('viewing');
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('limit')) toast.error('Daily flashcard limit reached!');
      else toast.error('Failed to generate flashcards');
    }
    setLoading(false);
  };

  const openSet = (set) => {
    setActiveSet(set);
    setCardIdx(0);
    setFlipped(false);
    setPhase('viewing');
  };

  // LIST phase
  if (phase === 'list') {
    return (
      <div className="max-w-lg mx-auto px-4 pt-6" data-testid="flashcards-list-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button data-testid="flashcards-back-btn" onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h1 className="font-heading text-xl font-bold text-slate-900">Flashcards</h1>
          </div>
          <Button data-testid="new-flashcard-btn" onClick={() => setPhase('select')} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full font-heading text-sm px-4">
            + New Set
          </Button>
        </div>

        {/* Phase 2.70 — chapter-scope chip */}
        <ActiveScopeChip
          subject={scopeSubject}
          topic={scopeTopic}
          onClear={() => { setScopeSubject(''); setScopeTopic(''); }}
        />

        {autoHydrating ? (
          <BuildingDeckSplash kind="flashcards" subject={scopeSubject} topic={scopeTopic} />
        ) : sets.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500 font-body mb-4">No flashcard sets yet</p>
            <Button data-testid="create-first-flashcard" onClick={() => setPhase('select')} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full font-heading">
              Create Your First Set
            </Button>
          </div>
        ) : (
          <div className="space-y-3 stagger-children">
            {sets.map(s => (
              <button key={s.set_id} data-testid={`flashcard-set-${s.set_id}`} onClick={() => openSet(s)} className="w-full text-left">
                <Card className="p-4 rounded-2xl border-slate-100 hover:border-amber-200 transition-all">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-heading text-sm font-semibold text-slate-800">{s.topic}</p>
                      <p className="text-xs text-slate-500 font-body">{s.subject} &middot; {s.cards?.length || 0} cards</p>
                    </div>
                    <Badge className="bg-amber-50 text-amber-600 text-xs">{s.cards?.length} cards</Badge>
                  </div>
                </Card>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // SELECT phase
  if (phase === 'select') {
    return (
      <div className="max-w-lg mx-auto px-4 pt-6" data-testid="flashcards-select-page">
        <div className="flex items-center gap-3 mb-6">
          <button data-testid="flashcards-select-back" onClick={() => setPhase('list')} className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="font-heading text-xl font-bold text-slate-900">Generate Flashcards</h1>
        </div>
        {/* Phase 2.71 — same scope chip on the select-new-set screen */}
        <ActiveScopeChip
          subject={selectedSubject}
          topic={selectedTopic}
          onClear={() => { setSelectedSubject(''); setSelectedTopic(''); }}
        />
        <div className="glass-panel rounded-3xl p-6 space-y-4">
          <select data-testid="fc-subject-select" value={selectedSubject} onChange={e => { setSelectedSubject(e.target.value); setSelectedTopic(''); }}
            className="w-full p-3 rounded-2xl border-2 border-slate-200 bg-white text-sm font-body focus:border-amber-500 focus:outline-none">
            <option value="">Select Subject</option>
            {subjects.map(s => <option key={s.subject_id} value={s.name}>{s.name}</option>)}
          </select>
          {selectedSubject && (
            <select data-testid="fc-topic-select" value={selectedTopic} onChange={e => setSelectedTopic(e.target.value)}
              className="w-full p-3 rounded-2xl border-2 border-slate-200 bg-white text-sm font-body focus:border-amber-500 focus:outline-none">
              <option value="">Select Topic</option>
              {/* Phase 2.71 — preserve the pre-filled top-bar chapter
                  even when the canonical /onboarding/topics list doesn't
                  contain it (e.g. an admin-only syllabus chapter). */}
              {selectedTopic && !topics.some(t => t.name === selectedTopic) && (
                <option key="from-topbar" value={selectedTopic}>{selectedTopic} (from top bar)</option>
              )}
              {topics.map(t => <option key={t.topic_id} value={t.name}>{t.name}</option>)}
            </select>
          )}
          <Button data-testid="generate-flashcards-btn" onClick={generateSet} disabled={loading || !selectedSubject || !selectedTopic}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full font-heading">
            {loading ? 'Generating...' : 'Generate Flashcards'}
          </Button>
        </div>
      </div>
    );
  }

  // VIEWING phase
  if (phase === 'viewing' && activeSet) {
    const card = activeSet.cards?.[cardIdx];
    return (
      <div className="max-w-lg mx-auto px-4 pt-6" data-testid="flashcards-viewing-page">
        <div className="flex items-center justify-between mb-6">
          <button data-testid="fc-viewing-back" onClick={() => setPhase('list')} className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <span className="text-sm text-slate-500 font-body">{cardIdx + 1} / {activeSet.cards?.length}</span>
          <Badge className="bg-amber-50 text-amber-600">{activeSet.topic}</Badge>
        </div>

        {/* Flashcard */}
        <div className="flashcard-container mb-6" style={{ minHeight: 280 }}>
          <button data-testid="flashcard-flip" onClick={() => setFlipped(f => !f)} className="w-full text-left">
            <div className={`flashcard-inner relative ${flipped ? 'flipped' : ''}`} style={{ minHeight: 280 }}>
              <Card className="flashcard-front absolute inset-0 p-8 rounded-3xl border-slate-100 flex flex-col items-center justify-center bg-gradient-to-br from-white to-amber-50">
                <p className="text-xs text-amber-500 font-body mb-3 uppercase tracking-wider">Question</p>
                <p className="font-heading text-lg font-semibold text-slate-800 text-center leading-relaxed">{card?.front}</p>
                <p className="text-xs text-slate-400 font-body mt-4">Tap to flip</p>
              </Card>
              <Card className="flashcard-back absolute inset-0 p-8 rounded-3xl border-emerald-100 flex flex-col items-center justify-center bg-gradient-to-br from-white to-emerald-50">
                <p className="text-xs text-emerald-500 font-body mb-3 uppercase tracking-wider">Answer</p>
                <p className="font-body text-base text-slate-700 text-center leading-relaxed">{card?.back}</p>
                <p className="text-xs text-slate-400 font-body mt-4">Tap to flip back</p>
              </Card>
            </div>
          </button>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button data-testid="fc-prev" onClick={() => { setCardIdx(i => Math.max(0, i - 1)); setFlipped(false); }} disabled={cardIdx === 0}
            variant="outline" className="rounded-full font-heading">
            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
          </Button>
          <Button data-testid="fc-flip-btn" onClick={() => setFlipped(f => !f)} variant="ghost" className="rounded-full font-heading">
            <RotateCcw className="w-4 h-4 mr-1" /> Flip
          </Button>
          <Button data-testid="fc-next" onClick={() => { setCardIdx(i => Math.min(activeSet.cards.length - 1, i + 1)); setFlipped(false); }} disabled={cardIdx === activeSet.cards.length - 1}
            variant="outline" className="rounded-full font-heading">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
